'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined'
import BloodtypeOutlinedIcon from '@mui/icons-material/BloodtypeOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined'
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import StarIcon from '@mui/icons-material/Star'
import { Box, Collapse, Typography } from '@mui/material'
import { capitalizeWords } from '@utils/common'
import { getStyles } from '@utils/styles'
import defaultStyles from './styles'

type GenericTestSectionsProps = {
  cityName: string
  phase: 'before-guide' | 'after-guide'
  testName: string
  relatedContent?: Partial<{
    tests: { label: string; href: string }[]
    packages: { label: string; href: string }[]
    articles: { label: string; href: string }[]
  }>
}

const bundleCards = [
  {
    badge: 'Most Popular',
    title: 'Complete Health Screening',
    tests: [
      'Complete Blood Count',
      'Liver Function Test',
      'Kidney Function Test',
    ],
  },
  {
    badge: 'Popular',
    title: 'Essential Wellness Profile',
    tests: ['Blood Sugar', 'Lipid Profile', 'Thyroid Profile'],
  },
  {
    badge: 'Basic',
    title: 'Preventive Health Check',
    tests: ['CBC', 'Urine Examination', 'Blood Glucose'],
  },
]

const faqs = [
  [
    'What is this test used for?',
    'This test helps assess relevant health markers. Your doctor can interpret the result together with symptoms, history and other investigations.',
  ],
  [
    'Is fasting required before the test?',
    'Preparation requirements can vary. Follow the instructions shown during booking or those provided by your doctor or Apollo Diagnostics.',
  ],
  [
    'Can I book a home sample collection?',
    'Home sample collection is available in supported locations. Availability is confirmed during booking based on your selected city and address.',
  ],
  [
    'How will I receive my report?',
    'Reports are made available digitally after laboratory processing is complete. Report time can vary by test and location.',
  ],
  [
    'Should I consult a doctor about the result?',
    'A healthcare professional should interpret results that are abnormal, unexpected or associated with symptoms.',
  ],
]

export function GenericTestSections({
  cityName,
  phase,
  testName,
  relatedContent,
}: GenericTestSectionsProps) {
  const styles = getStyles(defaultStyles)
  const [openFaq, setOpenFaq] = useState(0)
  const city = capitalizeWords(cityName)
  const citySlug = cityName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
  const relatedPackages = relatedContent?.packages || []
  const relatedArticles = relatedContent?.articles || []
  const relatedTests = relatedContent?.tests || []

  if (phase === 'before-guide') {
    return (
      <Box component="section" {...styles('parametersSection')}>
        <Box {...styles('container')}>
          <Box {...styles('parametersCard')}>
            <Box {...styles('sectionHeading')}>
              <Box {...styles('sectionIcon')}>
                <BiotechOutlinedIcon />
              </Box>
              <Box>
                <Typography component="h2" {...styles('sectionTitle')}>
                  Test parameters
                </Typography>
                <Typography {...styles('sectionSubtitle')}>
                  Parameters are reported according to the selected test and
                  laboratory protocol.
                </Typography>
              </Box>
            </Box>
            <Box {...styles('parameterGrid')}>
              {[
                'Clinical laboratory analysis',
                'Quality-controlled processing',
                'Reference ranges in report',
                'Doctor-ready digital report',
              ].map((parameter) => (
                <Box key={parameter} {...styles('parameterItem')}>
                  <CheckCircleOutlineIcon /> {parameter}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Box component="section" {...styles('bundleSection')}>
        <Box {...styles('container')}>
          <Typography component="h2" {...styles('sectionTitle')}>
            Our popular test bundles
          </Typography>
          <Typography {...styles('sectionSubtitle')}>
            Explore preventive profiles commonly booked with individual
            diagnostic tests.
          </Typography>
          <Box {...styles('cardGrid')}>
            {bundleCards.map((bundle) => (
              <Box key={bundle.title} {...styles('bundleCard')}>
                <Typography component="span" {...styles('badge')}>
                  {bundle.badge}
                </Typography>
                <Typography component="h3" {...styles('cardTitle')}>
                  {bundle.title}
                </Typography>
                <Box {...styles('list')}>
                  {bundle.tests.map((test) => (
                    <Typography key={test}>✓ {test}</Typography>
                  ))}
                </Box>
                <Link
                  href={`/health-check-packages/${citySlug}`}
                  style={{ textDecoration: 'none' }}
                >
                  <Box component="span" {...styles('outlineButton')}>
                    View packages
                  </Box>
                </Link>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box component="section" {...styles('alternateSection')}>
        <Box {...styles('container')}>
          <Typography component="h2" {...styles('sectionTitle')}>
            {testName} availability by city
          </Typography>
          <Typography {...styles('sectionSubtitle')}>
            Pricing and home collection availability may vary by city. Select
            your location to view the current booking options.
          </Typography>
          <Box {...styles('cityGrid')}>
            {[city, 'Hyderabad', 'Delhi', 'Mumbai', 'Bengaluru', 'Chennai']
              .filter((value, index, values) => values.indexOf(value) === index)
              .map((name, index) => (
                <Box
                  key={name}
                  {...styles(
                    index === 0 ? ['cityCard', 'activeCityCard'] : 'cityCard'
                  )}
                >
                  <LocationOnOutlinedIcon />
                  <Box>
                    <Typography {...styles('cityName')}>{name}</Typography>
                    <Typography {...styles('cityCopy')}>
                      {index === 0 ? 'Selected location' : 'Check availability'}
                    </Typography>
                  </Box>
                </Box>
              ))}
          </Box>
        </Box>
      </Box>

      <Box component="section" {...styles('section')}>
        <Box {...styles('container')}>
          <Typography component="h2" {...styles('sectionTitle')}>
            What our patients say
          </Typography>
          <Box {...styles('cardGrid')}>
            {[
              [
                'Simple home collection process and helpful staff.',
                'Verified patient',
              ],
              [
                'The booking experience was convenient and the report was easy to access.',
                'Verified patient',
              ],
              [
                'Professional sample collection with timely updates.',
                'Verified patient',
              ],
            ].map(([review, author]) => (
              <Box key={review} {...styles('reviewCard')}>
                <Box {...styles('stars')}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon key={star} />
                  ))}
                </Box>
                <Typography {...styles('reviewCopy')}>“{review}”</Typography>
                <Typography {...styles('reviewAuthor')}>{author}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box component="section" {...styles('faqSection')}>
        <Box {...styles(['container', 'narrowContainer'])}>
          <Typography component="h2" {...styles('sectionTitle')}>
            Frequently asked questions
          </Typography>
          <Box {...styles('faqList')}>
            {faqs.map(([question, answer], index) => (
              <Box key={question} {...styles('faqItem')}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                  {...styles('faqButton')}
                >
                  <Typography>{question}</Typography>
                  <ExpandMoreIcon
                    {...styles(
                      openFaq === index ? ['faqIcon', 'faqIconOpen'] : 'faqIcon'
                    )}
                  />
                </Box>
                <Collapse in={openFaq === index}>
                  <Typography {...styles('faqAnswer')}>{answer}</Typography>
                </Collapse>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box component="section" {...styles('section')}>
        <Box {...styles('container')}>
          <Typography component="h2" {...styles('sectionTitle')}>
            Related health packages
          </Typography>
          <Box {...styles('fourColumnGrid')}>
            {(relatedPackages.length
              ? relatedPackages
              : [
                  {
                    label: 'Full Body Checkup',
                    href: `/health-check-packages/${citySlug}`,
                  },
                  {
                    label: "Women's Health Package",
                    href: `/health-check-packages/${citySlug}`,
                  },
                  {
                    label: 'Senior Citizen Package',
                    href: `/health-check-packages/${citySlug}`,
                  },
                  {
                    label: 'Diabetes Care Package',
                    href: `/health-check-packages/${citySlug}`,
                  },
                ]
            ).map((item) => (
              <Box key={item.label} {...styles('relatedCard')}>
                <HealthAndSafetyOutlinedIcon />
                <Typography component="h3" {...styles('relatedTitle')}>
                  {item.label}
                </Typography>
                <Typography {...styles('relatedCopy')}>
                  Preventive health screening from Apollo Diagnostics.
                </Typography>
                <Link href={item.href.replace(':city', citySlug)}>
                  View package
                </Link>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box component="section" {...styles('alternateSection')}>
        <Box {...styles('container')}>
          <Typography component="h2" {...styles('sectionTitle')}>
            Related articles
          </Typography>
          <Box {...styles('fourColumnGrid')}>
            {(relatedArticles.length
              ? relatedArticles
              : [
                  {
                    label: 'Understanding your diagnostic report',
                    href: '/blogs',
                  },
                  { label: 'How to prepare for a blood test', href: '/blogs' },
                  { label: 'Why preventive screening matters', href: '/blogs' },
                  {
                    label: 'Benefits of home sample collection',
                    href: '/blogs',
                  },
                ]
            ).map((item) => (
              <Box key={item.label} {...styles('articleCard')}>
                <AssignmentTurnedInOutlinedIcon />
                <Typography component="h3" {...styles('relatedTitle')}>
                  {item.label}
                </Typography>
                <Link href={item.href}>Read article</Link>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box component="section" {...styles('section')}>
        <Box {...styles('container')}>
          <Typography component="h2" {...styles('sectionTitle')}>
            Related tests
          </Typography>
          <Box {...styles('relatedTests')}>
            {(relatedTests.length
              ? relatedTests
              : [
                  { label: 'Complete Blood Count', href: '/tests' },
                  { label: 'Liver Function Test', href: '/tests' },
                  { label: 'HbA1c Diabetes Test', href: '/tests' },
                  { label: 'Lipid Profile Test', href: '/tests' },
                  { label: 'Thyroid Profile', href: '/tests' },
                  { label: 'Complete Urine Examination', href: '/tests' },
                ]
            ).map((item) => (
              <Link
                key={item.label}
                href={item.href}
                style={{ textDecoration: 'none' }}
              >
                <Box {...styles('testCard')}>
                  <ScienceOutlinedIcon />
                  <Typography>{item.label}</Typography>
                </Box>
              </Link>
            ))}
          </Box>
        </Box>
      </Box>

      <Box component="section" {...styles('finalCta')}>
        <Box {...styles(['container', 'ctaContent'])}>
          <Box>
            <Typography component="h2">
              Book {testName} with Apollo Diagnostics
            </Typography>
            <Typography>
              Trusted diagnostic testing with convenient home sample collection.
            </Typography>
          </Box>
          <Box {...styles('ctaButtons')}>
            <Link
              href={`/book-a-test/${citySlug}`}
              style={{ textDecoration: 'none' }}
            >
              <Box component="span" {...styles('primaryButton')}>
                <BloodtypeOutlinedIcon /> Book a test
              </Box>
            </Link>
            <Box
              component="a"
              href="tel:04069467075"
              {...styles('secondaryButton')}
            >
              <PhoneOutlinedIcon /> Call to book
            </Box>
          </Box>
        </Box>
      </Box>

      <Box {...styles('mobileActions')}>
        <Link
          href={`/book-a-test/${citySlug}`}
          style={{ width: '50%', textDecoration: 'none' }}
        >
          <Box {...styles('mobileBook')}>
            <LocalHospitalOutlinedIcon /> Book a test
          </Box>
        </Link>
        <Box component="a" href="tel:04069467075" {...styles('mobileCall')}>
          <PhoneOutlinedIcon /> Call to book
        </Box>
      </Box>
    </>
  )
}
